import { useState, useEffect, useCallback } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, Phone, MessageCircle, Calendar, ChevronRight, MapPin, Sparkles } from 'lucide-react';
import { Button } from '@/components/ui/button';
import ThemeToggle from '@/components/ThemeToggle';
import OptimizedImage from '@/components/ui/OptimizedImage';
import logo from '@/assets/logo.png';

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Lock body scroll when menu is open
  useEffect(() => {
    if (isMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isMenuOpen]);

  // Close menu on route change
  useEffect(() => {
    setIsMenuOpen(false);
  }, [location.pathname]);

  const closeMenu = useCallback(() => setIsMenuOpen(false), []);
  const toggleMenu = useCallback(() => setIsMenuOpen(prev => !prev), []);

  const navLinks = [
    { name: 'iPhone', path: '/iphone-service', icon: '📱' },
    { name: 'MacBook', path: '/macbook-service', icon: '💻' },
    { name: 'iPad', path: '/ipad-service', icon: '📲' },
    { name: 'Apple Watch', path: '/watch-service', icon: '⌚' },
    { name: 'Blog', path: '/blog', icon: '📰' },
    { name: 'Franchise', path: '/franchise', icon: '🤝' },
  ];

  const quickActions = [
    { name: 'Service Areas', path: '/service-areas', icon: MapPin, description: 'All Bihar districts' },
    { name: 'Updates', path: '/updates', icon: Sparkles, description: 'Latest news' },
  ];

  const isActive = (path: string) => location.pathname === path;

  return (
    <>
      {/* Header Bar */}
      <header 
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          isScrolled 
            ? 'bg-gradient-to-r from-[#d1d1d6]/95 via-[#c7c7cc]/90 to-[#d1d1d6]/95 dark:from-[#2c2c2e]/95 dark:via-[#3a3a3c]/90 dark:to-[#2c2c2e]/95 backdrop-blur-2xl shadow-lg shadow-black/5' 
            : 'bg-gradient-to-r from-[#d1d1d6]/85 via-[#c7c7cc]/80 to-[#d1d1d6]/85 dark:from-[#2c2c2e]/85 dark:via-[#3a3a3c]/80 dark:to-[#2c2c2e]/85 backdrop-blur-xl'
        }`}
      >
        {/* Shine effect */}
        <div className="absolute inset-0 bg-gradient-to-b from-white/30 via-transparent to-transparent dark:from-white/10 pointer-events-none" />
        {/* Bottom border glow */}
        <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-white/60 dark:via-white/30 to-transparent" />
        <div className="absolute -bottom-1 left-1/4 right-1/4 h-2 bg-gradient-to-r from-transparent via-white/20 dark:via-white/10 to-transparent blur-sm" />
        
        <div className="container-apple relative">
          {/* Desktop Header */}
          <div className="hidden lg:flex items-center justify-between h-18 lg:h-20">
            {/* Logo */}
            <Link 
              to="/" 
              className="flex items-center gap-3 group animate-fade-in relative z-10 shrink-0"
              onClick={closeMenu}
            >
              <OptimizedImage 
                src={logo} 
                alt="iFix Apple Care - Apple Service Center Patna Bihar Logo" 
                className="h-12 lg:h-14 w-auto object-contain transition-transform duration-300 group-hover:scale-105"
                priority
              />
              <div className="flex flex-col leading-tight">
                <span className="font-bold text-foreground text-base lg:text-lg tracking-tight">iFix</span>
                <span className="text-muted-foreground text-[10px] lg:text-xs">Apple Service Center</span>
              </div>
            </Link>

            {/* Desktop Navigation */}
            <nav className="flex items-center justify-center flex-1 mx-4">
              <div className="flex items-center gap-0.5 bg-secondary/50 rounded-full p-1">
                {navLinks.map((link) => (
                  <Link
                    key={link.path}
                    to={link.path}
                    className={`relative px-3 xl:px-4 py-2 text-sm font-medium transition-all duration-200 rounded-full whitespace-nowrap ${
                      isActive(link.path)
                        ? 'text-foreground bg-background shadow-sm'
                        : 'text-muted-foreground hover:text-foreground hover:bg-background/50'
                    }`}
                  >
                    {link.name}
                  </Link>
                ))}
              </div>
            </nav>

            {/* Desktop Actions */}
            <div className="flex items-center gap-1.5 shrink-0">
              <ThemeToggle />
              <div className="w-px h-5 bg-border/50 mx-1" />
              <Button variant="call" size="sm" className="h-9 px-3" asChild>
                <a href="tel:9798653591" className="gap-1.5">
                  <Phone className="w-3.5 h-3.5" />
                  <span className="text-xs font-medium">Call</span>
                </a>
              </Button>
              <Button variant="whatsapp" size="sm" className="h-9 px-3" asChild>
                <a
                  href="https://wa.me/919798653591?text=Hi,%20I%20need%20help%20with%20my%20Apple%20device"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="gap-1.5"
                >
                  <MessageCircle className="w-3.5 h-3.5" />
                  <span className="text-xs font-medium">WhatsApp</span>
                </a>
              </Button>
              <Button variant="apple-blue" size="sm" className="h-9 px-3" asChild>
                <Link to="/#book" className="gap-1.5">
                  <Calendar className="w-3.5 h-3.5" />
                  <span className="text-xs font-medium">Book</span>
                </Link>
              </Button>
            </div>
          </div>

          {/* Mobile Header - Premium Redesign */}
          <div className="lg:hidden">
            {/* Top row: Logo centered with menu button */}
            <div className="flex items-center justify-between h-16">
              {/* Left: Theme toggle */}
              <div className="w-10">
                <ThemeToggle />
              </div>

              {/* Center: Large Logo + Brand Name */}
              <Link 
                to="/" 
                className="flex items-center gap-2.5 group relative z-10"
                onClick={closeMenu}
              >
                <div className="relative">
                  {/* Glow effect behind logo */}
                  <div className="absolute inset-0 bg-primary/20 blur-xl rounded-full scale-150 opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                  <OptimizedImage 
                    src={logo} 
                    alt="iFix Apple Care Logo" 
                    className="h-11 w-auto object-contain relative z-10 transition-transform duration-300 group-hover:scale-110"
                    priority
                  />
                </div>
                <div className="flex flex-col leading-none">
                  <span className="font-bold text-foreground text-lg tracking-tight">iFix</span>
                  <span className="text-muted-foreground text-[9px] font-medium tracking-wide uppercase">Apple Service Center</span>
                </div>
              </Link>

              {/* Right: Menu button */}
              <button
                type="button"
                className={`relative p-2.5 rounded-xl transition-all duration-300 z-[60] ${
                  isMenuOpen 
                    ? 'bg-primary text-primary-foreground shadow-lg shadow-primary/30' 
                    : 'bg-secondary/70 hover:bg-secondary'
                }`}
                onClick={toggleMenu}
                aria-label={isMenuOpen ? 'Close menu' : 'Open menu'}
                aria-expanded={isMenuOpen}
              >
                <div className="relative w-5 h-5">
                  <Menu 
                    className={`w-5 h-5 absolute inset-0 transition-all duration-300 ${
                      isMenuOpen ? 'opacity-0 rotate-90 scale-50' : 'opacity-100 rotate-0 scale-100'
                    }`} 
                  />
                  <X 
                    className={`w-5 h-5 absolute inset-0 transition-all duration-300 ${
                      isMenuOpen ? 'opacity-100 rotate-0 scale-100' : 'opacity-0 -rotate-90 scale-50'
                    }`} 
                  />
                </div>
              </button>
            </div>

            {/* Bottom row: Quick action buttons - always visible */}
            <div className="flex items-center justify-center gap-2 pb-2 -mt-1">
              <Button 
                variant="outline" 
                size="sm" 
                className="h-8 px-3 rounded-full text-xs font-medium border-green-500/30 bg-green-500/10 text-green-600 dark:text-green-400 hover:bg-green-500/20" 
                asChild
              >
                <a href="tel:9798653591" className="gap-1.5">
                  <Phone className="w-3 h-3" />
                  <span>Call</span>
                </a>
              </Button>
              <Button 
                variant="outline" 
                size="sm" 
                className="h-8 px-3 rounded-full text-xs font-medium border-emerald-500/30 bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 hover:bg-emerald-500/20" 
                asChild
              >
                <a
                  href="https://wa.me/919798653591?text=Hi,%20I%20need%20help%20with%20my%20Apple%20device"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="gap-1.5"
                >
                  <MessageCircle className="w-3 h-3" />
                  <span>WhatsApp</span>
                </a>
              </Button>
              <Button 
                variant="outline" 
                size="sm" 
                className="h-8 px-3 rounded-full text-xs font-medium border-primary/30 bg-primary/10 text-primary hover:bg-primary/20" 
                asChild
              >
                <Link to="/#book" className="gap-1.5">
                  <Calendar className="w-3 h-3" />
                  <span>Book</span>
                </Link>
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Mobile Menu - Premium Slide-in Panel */}
      {isMenuOpen && (
        <div className="lg:hidden fixed inset-0 z-[55]" aria-modal="true" role="dialog">
          {/* Backdrop with blur */}
          <div 
            className="absolute inset-0 bg-background/80 backdrop-blur-md animate-fade-in"
            onClick={closeMenu}
            aria-hidden="true"
          />
          
          {/* Menu Panel - Full screen overlay */}
          <div className="absolute top-[88px] left-0 right-0 bottom-0 bg-background overflow-y-auto overscroll-contain animate-slide-in-right">
            {/* Premium gradient header */}
            <div className="absolute top-0 left-0 right-0 h-40 bg-gradient-to-b from-primary/10 via-primary/5 to-transparent pointer-events-none" />
            
            <div className="relative px-5 py-6 pb-32">
              {/* Welcome section */}
              <div 
                className="text-center mb-8 opacity-0 animate-[fade-in_0.4s_ease-out_forwards]"
                style={{ animationDelay: '50ms' }}
              >
                <div className="inline-flex items-center gap-2 px-4 py-2 bg-primary/10 rounded-full mb-3">
                  <span className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />
                  <span className="text-xs font-medium text-primary">Open Now</span>
                </div>
                <h2 className="text-xl font-semibold text-foreground">How can we help?</h2>
              </div>

              {/* Services Grid - Card Style */}
              <div 
                className="mb-8 opacity-0 animate-[fade-in_0.4s_ease-out_forwards]"
                style={{ animationDelay: '100ms' }}
              >
                <p className="text-[10px] font-bold text-muted-foreground uppercase tracking-widest mb-4 px-1">
                  Our Services
                </p>
                <div className="grid grid-cols-2 gap-3">
                  {navLinks.slice(0, 4).map((link, index) => (
                    <Link
                      key={link.path}
                      to={link.path}
                      onClick={closeMenu}
                      className={`group relative flex flex-col items-center justify-center p-5 rounded-2xl transition-all duration-300 opacity-0 animate-[scale-in_0.4s_ease-out_forwards] ${
                        isActive(link.path)
                          ? 'bg-primary text-primary-foreground shadow-xl shadow-primary/25'
                          : 'bg-secondary/60 hover:bg-secondary active:scale-[0.97]'
                      }`}
                      style={{ animationDelay: `${150 + index * 75}ms` }}
                    >
                      <span className="text-3xl mb-2" role="img" aria-hidden="true">{link.icon}</span>
                      <span className="font-semibold text-sm">{link.name}</span>
                      <ChevronRight className={`w-4 h-4 absolute top-3 right-3 transition-all duration-200 ${
                        isActive(link.path) ? 'text-primary-foreground/70' : 'text-muted-foreground/50 group-hover:translate-x-0.5'
                      }`} />
                    </Link>
                  ))}
                </div>
              </div>

              {/* Quick Links - Horizontal scroll style */}
              <div 
                className="mb-8 opacity-0 animate-[fade-in_0.4s_ease-out_forwards]"
                style={{ animationDelay: '450ms' }}
              >
                <p className="text-[10px] font-bold text-muted-foreground uppercase tracking-widest mb-4 px-1">
                  Quick Links
                </p>
                <div className="space-y-2">
                  {navLinks.slice(4).map((link, index) => (
                    <Link
                      key={link.path}
                      to={link.path}
                      onClick={closeMenu}
                      className={`group flex items-center justify-between px-4 py-3.5 rounded-xl transition-all duration-200 opacity-0 animate-[slide-in-right_0.4s_ease-out_forwards] ${
                        isActive(link.path)
                          ? 'bg-primary/15 border border-primary/30'
                          : 'bg-secondary/40 hover:bg-secondary/70 active:scale-[0.99]'
                      }`}
                      style={{ animationDelay: `${500 + index * 75}ms` }}
                    >
                      <div className="flex items-center gap-3">
                        <span className="text-xl" role="img" aria-hidden="true">{link.icon}</span>
                        <span className="font-medium text-sm text-foreground">{link.name}</span>
                      </div>
                      <ChevronRight className="w-4 h-4 text-muted-foreground/50 group-hover:translate-x-0.5 transition-transform" />
                    </Link>
                  ))}
                  {quickActions.map((action, index) => (
                    <Link
                      key={action.path}
                      to={action.path}
                      onClick={closeMenu}
                      className={`group flex items-center justify-between px-4 py-3.5 rounded-xl transition-all duration-200 opacity-0 animate-[slide-in-right_0.4s_ease-out_forwards] ${
                        isActive(action.path)
                          ? 'bg-primary/15 border border-primary/30'
                          : 'bg-secondary/40 hover:bg-secondary/70 active:scale-[0.99]'
                      }`}
                      style={{ animationDelay: `${650 + index * 75}ms` }}
                    >
                      <div className="flex items-center gap-3">
                        <action.icon className="w-5 h-5 text-primary" />
                        <div className="flex flex-col">
                          <span className="font-medium text-sm text-foreground">{action.name}</span>
                          <span className="text-[10px] text-muted-foreground">{action.description}</span>
                        </div>
                      </div>
                      <ChevronRight className="w-4 h-4 text-muted-foreground/50 group-hover:translate-x-0.5 transition-transform" />
                    </Link>
                  ))}
                </div>
              </div>

              {/* Contact Actions - Large CTAs */}
              <div 
                className="space-y-3 opacity-0 animate-[fade-in_0.5s_ease-out_forwards]"
                style={{ animationDelay: '750ms' }}
              >
                <p className="text-[10px] font-bold text-muted-foreground uppercase tracking-widest mb-4 px-1">
                  Get in Touch
                </p>
                
                <div className="grid grid-cols-2 gap-3">
                  <Button 
                    variant="call" 
                    className="h-16 rounded-2xl text-sm font-semibold shadow-lg shadow-green-500/20 flex-col gap-1 opacity-0 animate-[scale-in_0.4s_ease-out_forwards]" 
                    style={{ animationDelay: '800ms' }}
                    asChild
                  >
                    <a href="tel:9798653591">
                      <Phone className="w-5 h-5" />
                      <span>Call Now</span>
                    </a>
                  </Button>
                  <Button 
                    variant="whatsapp" 
                    className="h-16 rounded-2xl text-sm font-semibold shadow-lg shadow-emerald-500/20 flex-col gap-1 opacity-0 animate-[scale-in_0.4s_ease-out_forwards]" 
                    style={{ animationDelay: '875ms' }}
                    asChild
                  >
                    <a
                      href="https://wa.me/919798653591?text=Hi,%20I%20need%20help%20with%20my%20Apple%20device"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <MessageCircle className="w-5 h-5" />
                      <span>WhatsApp</span>
                    </a>
                  </Button>
                </div>
                
                <Button 
                  variant="apple-blue" 
                  className="w-full h-14 rounded-2xl text-sm font-semibold shadow-lg shadow-primary/25 gap-2 opacity-0 animate-[scale-in_0.4s_ease-out_forwards]" 
                  style={{ animationDelay: '950ms' }}
                  asChild
                >
                  <Link to="/#book" onClick={closeMenu}>
                    <Calendar className="w-5 h-5" />
                    Book Your Appointment
                  </Link>
                </Button>
              </div>

              {/* Footer Info - Premium styling */}
              <div 
                className="mt-10 pt-6 border-t border-border/30 opacity-0 animate-[fade-in_0.5s_ease-out_forwards]"
                style={{ animationDelay: '1050ms' }}
              >
                <div className="flex items-center justify-center gap-6 text-xs text-muted-foreground">
                  <div className="flex items-center gap-1.5">
                    <MapPin className="w-3.5 h-3.5 text-primary" />
                    <span>Boring Road, Patna</span>
                  </div>
                  <div className="w-1 h-1 rounded-full bg-muted-foreground/30" />
                  <div className="flex items-center gap-1.5">
                    <span>⏰</span>
                    <span>10 AM - 8 PM</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Header;